<template>
  <BaseCard
    title="Average Multiple by Industry Sector"
    class="h-full"
  >
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else-if="analytics.length > 0"
            class="flex flex-col gap-4 w-full  rounded-lg"
          >
            <Bar
              id="averageMultipleChart"
              class="w-full"
              :options="chartOptions"
              :data="chartData"
            />
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center p-12 rounded-lg"
          >
            <Icon
              name="mdi:chart-line"
              size="48"
              class="text-gray-300 mb-4"
            />
            <p class="text-gray-500 text-center">
              No business data available to display
            </p>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import type { ChartOptions } from 'chart.js'
import { useDashboard } from '~/composables/useDashboardCA'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface IndustryAnalytics {
  sectorName: string
  sectorId: string
  totalBusinesses: number
  totalPrice: number
  averagePrice: number
  averageMultiple: number
}

interface AnalyticsResponse {
  analytics: IndustryAnalytics[]
  summary: unknown
}

const { getIndustryAnalytics } = useDashboard()

const isLoading = ref(true)
const analytics = ref<IndustryAnalytics[]>([])

const formatMultiple = (value: number): string => {
  if (!value || value === 0) return '0.00x'
  return `${value.toFixed(2)}x`
}

const chartOptions = ref<ChartOptions<'bar'>>({
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      display: false,
      position: 'top' as const,
    },
    title: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        label: (context: any) => {
          const value = context.parsed.y
          if (value === null || value === undefined) return ''
          return formatMultiple(value)
        },
      },
    },
  },
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
    y: {
      display: true,
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value: number | string) => {
          const numValue = typeof value === 'string' ? parseFloat(value) : value
          return `${numValue.toFixed(2)}x`
        },
      },
    },
  },
  animation: {
    duration: 2000,
    easing: 'easeInOutQuart' as const,
  },
})

const chartData = computed(() => {
  if (analytics.value.length === 0) {
    return {
      labels: [],
      datasets: [
        {
          label: '',
          backgroundColor: 'rgba(151, 186, 159, 0.8)',
          borderColor: 'rgba(151, 186, 159, 1)',
          borderWidth: 1,
          data: [],
        },
      ],
    }
  }

  const primaryColor = getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim() || 'rgba(151, 186, 159, 1)'

  const labels = analytics.value.map(item => item.sectorName)
  const data = analytics.value.map(item => item.averageMultiple)

  return {
    labels,
    datasets: [
      {
        label: 'Average Multiple',
        backgroundColor: `${primaryColor}CC`,
        borderColor: primaryColor,
        borderWidth: 1,
        data,
      },
    ],
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getIndustryAnalytics()
    const data = response?.data as AnalyticsResponse

    if (data) {
      analytics.value = data.analytics || []
    }
  }
  catch (error) {
    console.error('Error fetching industry analytics chart:', error)
    analytics.value = []
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
